import {
  faBell,
  faCancel,
  faCheck,
  faClone,
  faDeleteRight,
  faInfoCircle,
  faLink,
  faPen,
  faSend,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ButtonV2,
  ColumnActionWithMenuColors,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
  VerticalDivider,
} from '@labrav/react-components'
import { useStyles } from './OfferHeaderActions.styles'
import {
  OfferStatus,
  PrivateOffer,
} from '../../../../modules/privateOffer/types'
import { useMemo, useState } from 'react'
import { useAuth } from '@labrav/react-components'
import { Typography } from '@mui/material'
import { useDispatch, useSelector } from '../../../../store'
import {
  cloneMicrosoftPrivateOffer,
  clonePrivateOffer,
  deleteCurrentPrivateOffer,
  resetMicrosoftPrivateOffer,
  updatePrivateOfferForUser,
  sendReminder,
  resendOffer,
} from '../../../../modules/privateOffer/action'
import { useNavigate, useParams } from 'react-router-dom'
import { OfferReminderTheBuyer } from '../../OfferReminderTheBuyer/OfferReminderTheBuyer'
import { OfferResendConfirmation } from '../../OfferResendConfirmation/OfferResendConfirmation'
import {
  Product,
  ProductStatus,
  CloudMarketplace,
} from '../../../../modules/productsListing/types'
import { isEmpty, noop } from 'lodash'
import clsx from 'clsx'
import {
  copyToClipboard,
  LoadingTypes,
  isLoading as Loading,
  useAccountId,
} from '@labrav/common'
import { getDeleteOfferDisabledTooltip } from '../../../../utils/getDeleteOfferDisabledTooltip'
import { getClonePurchaseTermsFromOffer } from '../../../../modules/privateOffer/privateOfferCreation/utils/microsoftPurchaseTermsUtil'
import { CRMTypes } from '../../../../utils/constants'
import { OfferHeaderIcon } from '../../../../components/OfferHeaderIcon/OfferHeaderIcon'
import {
  DownloadDetailPdfButton,
  DetailPdfCloud,
  DetailPdfPageType,
} from '../../DownloadDetailPdfButton'

export type PrivateOfferViewTrackingHandlers = {
  trackHeaderAction: (params: {
    actionKey: string
    privateOfferId?: string
    cloudType?: string
    offerStatus?: string
  }) => void
}

type OfferHeaderActionsProps = {
  privateOffer: PrivateOffer
  crmOpportunityId?: string
  product?: Product
  cloudMarketplace: CloudMarketplace
  externalBaseURL?: string
  search?: string
  planType?: PlanType
  crmType?: CRMTypes
  partnerId?: string
  viewTracking?: PrivateOfferViewTrackingHandlers
}

export const OfferHeaderActions = ({
  privateOffer,
  crmOpportunityId,
  product,
  cloudMarketplace,
  externalBaseURL,
  search,
  planType,
  crmType,
  partnerId,
  viewTracking,
}: OfferHeaderActionsProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const params = useParams<{
    cloud?: string
    accountId?: string
    productId?: string
    privateOfferId?: string
    secureKey?: string
    partnerId?: string
  }>()

  const {
    cloud: routeCloud,
    accountId: paramAccountId,
    productId: paramProductId,
  } = params

  const productId =
    paramProductId ||
    product?.productId ||
    privateOffer?.labraProductId ||
    privateOffer?.cloudProductId ||
    privateOffer?.awsProductId ||
    undefined

  const routeAccountId = partnerId || paramAccountId || accountId || ''
  const crmObjectLink = search
    ? new URLSearchParams(search).get('object_link') || undefined
    : undefined

  const { privateOfferId, offerStatus } = privateOffer
  const [copiedOfferLink, setCopiedOfferLink] = useState(false)

  const [openCloneModal, setOpenCloneModal] = useState(false)
  const [openEditOfferModal, setOpenEditOfferModal] = useState(false)

  // Construct base URLs using the new route structure
  const privateOffersListURL = useMemo(() => {
    if (externalBaseURL) {
      if (externalBaseURL.endsWith('/product-listing')) {
        return `${externalBaseURL}/offers-authorizations/private-offers`
      } else {
        const productListingIndex = externalBaseURL.indexOf('/product-listing')
        if (productListingIndex !== -1) {
          const basePath = externalBaseURL.substring(
            0,
            productListingIndex + '/product-listing'.length
          )
          return `${basePath}/offers-authorizations/private-offers`
        }
        const baseWithoutProductId = externalBaseURL.replace(/\/[^/]+$/, '')
        return `${baseWithoutProductId}/offers-authorizations/private-offers`
      }
    }
    if (routeCloud && routeAccountId) {
      return `/flyout/${routeCloud}/${routeAccountId}/offers-authorizations/private-offers`
    }
    return '/flyout/offers-authorizations/private-offers'
  }, [externalBaseURL, routeCloud, routeAccountId])

  const productBaseURL = useMemo(() => {
    if (externalBaseURL) return externalBaseURL
    if (routeCloud && routeAccountId) {
      return `/flyout/${routeCloud}/${routeAccountId}/products-listing`
    }
    return `/flyout/products-listing`
  }, [externalBaseURL, routeCloud, routeAccountId])

  const handleClickEditFailedPO = () => {
    setOpenEditOfferModal(true)
  }
  const handleEditOfferCloseModal = () => {
    setOpenEditOfferModal(false)
  }

  const trackHeaderAction = (actionKey: string) => {
    viewTracking?.trackHeaderAction({
      actionKey,
      privateOfferId: privateOffer.privateOfferId,
      cloudType: cloudMarketplace,
      offerStatus,
    })
  }

  const handleOpenCloneModal = () => {
    trackHeaderAction('clone')
    setOpenCloneModal(true)
  }

  const handleCloseCloneModal = () => {
    setOpenCloneModal(false)
  }

  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )

  const isLoadingSubmit = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_FLYOUT_OFFER)
  )

  const [openCancelModal, setOpenCancelModal] = useState(false)

  const handleCloseCancelModal = () => {
    setOpenCancelModal(false)
  }

  const handleOpenCancelModal = () => {
    trackHeaderAction('cancel')
    setOpenCancelModal(true)
  }
  const [openReminderTheBuyerModal, setOpenReminderTheBuyerModal] =
    useState(false)

  const [
    openOfferResendConfirmationModal,
    setOpenOfferResendConfirmationModal,
  ] = useState(false)

  const isVisibleOfferLink =
    [
      OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
      OfferStatus.PRIVATE_OFFER_VIEWED,
      OfferStatus.PRIVATE_OFFER_EXPIRED,
      OfferStatus.OFFER_EXPIRED,
      OfferStatus.OFFER_EMAIL_SENT,
      OfferStatus.OFFER_VIEWED,

      OfferStatus.OFFER_TERMS_ACCEPTED_BUYER,
    ].includes(offerStatus) && privateOffer?.labraOfferUrl

  const handleOpenReminderTheBuyerModal = () => {
    trackHeaderAction('remind_the_buyer')
    setOpenReminderTheBuyerModal(true)
  }

  const offerLinkTooltipPlacement = copiedOfferLink
    ? 'top'
    : offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
      ? 'top-end'
      : 'top'

  const handleOpenOfferResendConfirmationModal = () => {
    setOpenOfferResendConfirmationModal(true)
  }

  const handleSubmitReminder = (data: Record<string, unknown>) => {
    dispatch(
      sendReminder({
        data: data,
        privateOfferId: privateOffer?.privateOfferId,
        callBack: () => setOpenReminderTheBuyerModal(false),
        partnerId: routeAccountId,
        cloudMarketplace: cloudMarketplace,
      })
    )
  }

  const handleSubmitResend = (data: Record<string, unknown>) => {
    dispatch(
      resendOffer({
        data: data,
        privateOfferId: privateOffer?.privateOfferId,
        callBack: () => setOpenOfferResendConfirmationModal(false),
        partnerId: routeAccountId,
        cloudMarketplace: cloudMarketplace,
      })
    )
  }

  const isVisibleCancel = [
    OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
    OfferStatus.PRIVATE_OFFER_VIEWED,

    OfferStatus.OFFER_EMAIL_SENT,
    OfferStatus.OFFER_VIEWED,
  ].includes(offerStatus)

  const isVisibleReminder =
    [
      OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
      OfferStatus.PRIVATE_OFFER_VIEWED,
    ].includes(offerStatus) &&
    (crmType === CRMTypes.salesforce ? true : isEmpty(externalBaseURL)) &&
    planType !== PlanType.AMI

  const isVisibleMoreMenu =
    cloudMarketplace === 'AWS' &&
    [
      OfferStatus.PRIVATE_OFFER_ACCEPTED,
      OfferStatus.PRIVATE_OFFER_FAILED,
      OfferStatus.PRIVATE_OFFER_EXPIRED,
      OfferStatus.PRIVATE_OFFER_RESTRCITED,
      OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
    ].includes(offerStatus) &&
    planType !== PlanType.AMI

  const handleConfirmCancelModal = async () => {
    await dispatch(
      updatePrivateOfferForUser(
        privateOffer?.privateOfferId,
        {
          ...(cloudMarketplace === 'AZURE'
            ? { state: 'draft', operation: 'UPDATE' }
            : {
                offerStatus: OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
              }),
        },
        crmOpportunityId,
        false,
        handleCloseCancelModal,
        cloudMarketplace,
        routeAccountId
      )
    )
  }

  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true)
  }

  const handleConfirmDeleteModal = async () => {
    await dispatch(
      deleteCurrentPrivateOffer(
        privateOfferId as string,
        () => setOpenDeleteModal(false),
        routeAccountId,
        cloudMarketplace
      )
    )
    navigate(privateOffersListURL, {
      state: {
        search,
      },
    })
  }

  const handleSelectOption = (option: ColumnMenuOption) => {
    if (option.key === 'delete-offer') {
      handleOpenDeleteModal()
    }
  }
  const { loading: authLoading, canDo } = useAuth()
  const canWriteMarketplace = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: (cloudMarketplace as string)?.toLowerCase(),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloudMarketplace])

  const permissionMessageString =
    'This action is disabled because you have read-only access'
  const copyOfferLink = async () => {
    trackHeaderAction('copy_offer_link')
    const link =
      cloudMarketplace === 'AWS'
        ? privateOffer?.labraOfferUrl
        : privateOffer?.acceptanceLink
    if (!link) {
      console.error('No offer link found')
      return
    }
    try {
      await copyToClipboard(link)
      setCopiedOfferLink(true)
      setTimeout(() => {
        setCopiedOfferLink(false)
      }, 2000)
    } catch (error) {
      console.error('Error copying URL to clipboard:', error)
    }
  }

  const handleRedirectCloneOffer = ({ offerId }: { offerId?: string }) => {
    if (!offerId) {
      console.error('No offerId provided for clone redirect')
      return
    }

    // For external URLs, use externalBaseURL directly which already contains productId
    if (externalBaseURL) {
      const cloneOfferPath = `${externalBaseURL}/private-offers/${offerId}/edit?from=clone`
      navigate(cloneOfferPath, {
        state: {
          search,
        },
      })
    } else if (productId) {
      // For internal URLs, use productBaseURL with productId
      const cloneOfferPath = `${productBaseURL}/${productId}/private-offers/${offerId}/edit?from=clone`
      navigate(cloneOfferPath, {
        state: {
          search,
        },
      })
    } else {
      console.error(
        'Cannot construct clone redirect path: missing productId or externalBaseURL'
      )
    }
  }
  const handleConfirmCloneModal = () => {
    if (productId) {
      if (cloudMarketplace === 'AWS') {
        dispatch(
          clonePrivateOffer({
            partnerId: routeAccountId,
            cloudMarketplace: cloudMarketplace,
            productId: productId,
            crmOpportunityId,
            crmObjectLink,
            privateOffer: privateOffer,
            callBack: handleRedirectCloneOffer,
          })
        )
      } else if (cloudMarketplace === 'AZURE') {
        dispatch(
          cloneMicrosoftPrivateOffer({
            partnerId: routeAccountId,
            cloneId: privateOfferId,
            originalOfferData: getClonePurchaseTermsFromOffer({
              variableStartDate: privateOffer.variableStartDate,
              offerStartDate: privateOffer.offerStartDate,
              offerExpirationDate: privateOffer.offerExpirationDate,
              createdAt: privateOffer.createdAt,
              metaData: privateOffer.metaData,
            }),
            callBack: handleRedirectCloneOffer,
          })
        )
      }
    }
  }

  const handleEditSuccess = () => {
    if (!productId) {
      console.error('Cannot navigate to edit: missing productId')
      return
    }

    // For external URLs, productBaseURL already contains productId
    if (externalBaseURL) {
      const editPath = `${productBaseURL}/private-offers/${privateOffer.privateOfferId}/edit`
      navigate(editPath, {
        state: {
          search,
        },
      })
    } else {
      // For internal URLs, use productBaseURL with productId
      navigate(
        `${productBaseURL}/${productId}/private-offers/${privateOffer.privateOfferId}/edit`,
        {
          state: {
            search,
          },
        }
      )
    }
  }

  const handleEditOfferConfirm = () => {
    if (cloudMarketplace === 'AWS') {
      dispatch(
        updatePrivateOfferForUser(
          privateOffer.privateOfferId,
          {},
          undefined,
          true,
          handleEditSuccess,
          cloudMarketplace,
          routeAccountId
        )
      )
    } else if (cloudMarketplace === 'AZURE') {
      dispatch(
        resetMicrosoftPrivateOffer({
          offerId: privateOfferId,
          callBack: handleEditSuccess,
          partnerId: routeAccountId,
        })
      )
    }
  }

  return (
    <div className={classes.root} data-testid={'offer-header-actions'}>
      <div className={classes.leftContainer}>
        <LabraToolTipV2
          tooltipPlacement="bottom"
          description={
            !canWriteMarketplace
              ? permissionMessageString
              : planType === PlanType.AMI
                ? 'Coming soon'
                : product?.status === ProductStatus.PRODUCT_STATUS_RESTRICTED
                  ? 'Cannot clone offer for restricted products.'
                  : ''
          }
        >
          <ButtonV2
            styleType="link"
            data-testid={'clone-offer'}
            onClick={handleOpenCloneModal}
            disabled={
              !canWriteMarketplace ||
              product?.status === ProductStatus.PRODUCT_STATUS_RESTRICTED
            }
            className={clsx(
              classes.linkItem,
              product?.status === ProductStatus.PRODUCT_STATUS_RESTRICTED ||
                planType === PlanType.AMI
                ? classes.btnDisabled
                : ''
            )}
          >
            <OfferHeaderIcon icon={faClone} />
            Clone
          </ButtonV2>
        </LabraToolTipV2>
        {offerStatus === OfferStatus.OFFER_CREATION_FAILED && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={!canWriteMarketplace ? permissionMessageString : ''}
            >
              <span>
                <ButtonV2
                  className={classes.linkItem}
                  smallVariant
                  styleType="link"
                  data-testid={'failed-offer-edit'}
                  onClick={handleClickEditFailedPO}
                  disabled={!canWriteMarketplace}
                >
                  <OfferHeaderIcon
                    icon={faPen}
                    style={{
                      width: '16px',
                      height: '16px',
                      padding: '0px 8px 0px 0px',
                    }}
                  />
                  Edit
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {isVisibleOfferLink && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              description={
                !canWriteMarketplace
                  ? permissionMessageString
                  : copiedOfferLink
                    ? 'Copied'
                    : offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                      ? `This offer has expired.${planType === PlanType.AMI ? '' : ' Extend the offer expiry date and then copy the offer link.'}`
                      : ''
              }
              tooltipPlacement={offerLinkTooltipPlacement}
              icon={
                copiedOfferLink &&
                offerStatus !== OfferStatus.PRIVATE_OFFER_EXPIRED ? (
                  <FontAwesomeIcon
                    icon={faCheck}
                    style={{ width: '12px', height: '12px' }}
                  />
                ) : undefined
              }
              tooltipPlacementGap="0"
            >
              <span>
                <ButtonV2
                  data-testid={'offer-link'}
                  styleType="link"
                  onClick={copyOfferLink}
                  disabled={!canWriteMarketplace}
                  className={
                    offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                      ? classes.btnDisabled
                      : ''
                  }
                >
                  <OfferHeaderIcon
                    icon={faLink}
                    style={{
                      width: '20px',
                      height: '16px',
                      padding: '0px 6px 0px 0px',
                    }}
                  />
                  Offer link
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {isVisibleCancel && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              tooltipPlacement="top"
              description={!canWriteMarketplace ? permissionMessageString : ''}
            >
              <span>
                <ButtonV2
                  styleType="link"
                  data-testid={'offer-cancel'}
                  onClick={handleOpenCancelModal}
                  disabled={!canWriteMarketplace}
                >
                  <OfferHeaderIcon icon={faCancel} />
                  Cancel
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {isVisibleReminder && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              tooltipPlacement="top"
              description={!canWriteMarketplace ? permissionMessageString : ''}
            >
              <span>
                <ButtonV2
                  styleType="link"
                  data-testid={'remind-the-buyer'}
                  onClick={handleOpenReminderTheBuyerModal}
                  disabled={!canWriteMarketplace}
                >
                  <OfferHeaderIcon icon={faBell} />
                  Remind the buyers
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED &&
          planType !== PlanType.AMI && (
            <>
              <VerticalDivider height="24px" />
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={
                  !canWriteMarketplace
                    ? permissionMessageString
                    : 'Resend offer confirmation email to the selected stakeholders.'
                }
              >
                <span>
                  <ButtonV2
                    styleType="link"
                    data-testid={'resend-offer-acceptance-email'}
                    onClick={handleOpenOfferResendConfirmationModal}
                    disabled={!canWriteMarketplace}
                  >
                    <OfferHeaderIcon icon={faSend} />
                    Resend offer acceptance email
                  </ButtonV2>
                </span>
              </LabraToolTipV2>
              <LabraToolTipV2
                tooltipPlacement="bottom"
                description={
                  'Resend offer confirmation email to the selected stakeholders.'
                }
              >
                <OfferHeaderIcon icon={faInfoCircle} color={'#6A777A'} />
              </LabraToolTipV2>
            </>
          )}
      </div>
      {(isVisibleMoreMenu || cloudMarketplace === 'AWS') && (
        <div className={classes.leftContainer}>
          {cloudMarketplace === 'AWS' && privateOfferId ? (
            <DownloadDetailPdfButton
              pageType={DetailPdfPageType.PRIVATE_OFFER}
              partnerId={routeAccountId}
              entityId={privateOfferId}
              cloud={DetailPdfCloud.AWS}
              testId="download-private-offer-pdf"
              onDownloadClick={() => trackHeaderAction('download_pdf')}
            />
          ) : null}
          {cloudMarketplace === 'AWS' && isVisibleMoreMenu && (
            <VerticalDivider height="24px" />
          )}
          {isVisibleMoreMenu && (
            <ColumnActionWithMenuV2
              iconColor={ColumnActionWithMenuColors.BLUE}
              columnTitle={<div className={classes.moreMenu}>More</div>}
              menuOptions={[
                {
                  key: 'delete-offer',
                  label: 'Delete offer',
                  disable:
                    !canWriteMarketplace ||
                    !(
                      offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED ||
                      offerStatus === OfferStatus.PRIVATE_OFFER_FAILED ||
                      offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED
                    ),
                  disabledTooltip: !canWriteMarketplace
                    ? permissionMessageString
                    : getDeleteOfferDisabledTooltip(privateOffer),
                },
              ]}
              handleSelectOption={handleSelectOption}
            />
          )}
        </div>
      )}

      <ModalConfirmV2
        title={'Cancel this private offer? '}
        description={
          <Typography className={classes.modalText}>
            <p>
              Your offer will be cancelled on the AWS Marketplace, and you will
              receive an email notification.
              <p>
                Please note that once cancelled, this offer cannot be restored.
              </p>
            </p>

            <p>Are you sure you want to cancel this offer?</p>
          </Typography>
        }
        open={openCancelModal}
        titleAcceptButton={'Confirm'}
        onAccept={handleConfirmCancelModal}
        acceptLoading={isLoading}
        onDecline={handleCloseCancelModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faDeleteRight}
            style={{ width: '20px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
        titleCancelButton={'Close'}
      />

      <ModalConfirmV2
        title={'Delete this private offer? '}
        description={
          <Typography className={classes.modalText}>
            <p>
              You will not be able to recover this offer or any information
              inside it once this offer is deleted.
            </p>

            <p>Are you sure you want to delete this offer?</p>
          </Typography>
        }
        open={openDeleteModal}
        titleAcceptButton={'Delete'}
        onAccept={handleConfirmDeleteModal}
        acceptLoading={isLoading}
        onDecline={handleCloseDeleteModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faTrash}
            style={{ width: '14px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
      />
      <OfferReminderTheBuyer
        offerId={privateOffer?.privateOfferId}
        offerRecipients={privateOffer?.offerRecipients}
        openReminderTheBuyerModal={openReminderTheBuyerModal}
        setOpenReminderTheBuyerModal={setOpenReminderTheBuyerModal}
        handleSubmit={handleSubmitReminder}
        cloudMarketplace={cloudMarketplace}
        partnerId={routeAccountId}
      />
      <OfferResendConfirmation
        offerId={privateOffer?.privateOfferId}
        offerStakeholders={privateOffer?.offerStakeholders}
        openOfferResendConfirmationModal={openOfferResendConfirmationModal}
        setOpenOfferResendConfirmationModal={
          setOpenOfferResendConfirmationModal
        }
        handleSubmit={handleSubmitResend}
        cloudMarketplace={cloudMarketplace}
        partnerId={routeAccountId}
      />
      <ModalConfirmV2
        title={'Clone private offer?'}
        content={
          <>
            <Typography className={classes.modalText}>
              <p>
                All data from this private offer will be copied to the new
                private offer form.
              </p>

              <p>
                <span className={classes.descriptionNote}>Note:</span> You can
                edit all information after cloning in the next step.
              </p>
            </Typography>
            <div className={classes.rowRightItem}>
              <ButtonV2
                data-testid="send-reminder"
                smallVariant
                styleType="outlined"
                onClick={handleCloseCloneModal}
              >
                Cancel
              </ButtonV2>
              <ButtonV2
                data-testid="send-reminder"
                smallVariant
                styleType="primary"
                onClick={handleConfirmCloneModal}
                loading={isLoadingSubmit}
              >
                Clone private offer
              </ButtonV2>
            </div>
          </>
        }
        open={openCloneModal}
        onDecline={handleCloseCloneModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faClone}
            style={{ width: '14px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={true}
        hideAcceptButton={true}
        onAccept={noop}
      />
      <ModalConfirmV2
        icon={
          <FontAwesomeIcon
            icon={faPen}
            style={{ width: '14px', height: '14px' }}
          />
        }
        title={'Edit this Offer?'}
        description={
          <Typography className={classes.modalText}>
            <p data-testid={'edit-offer-modal'}>
              Clicking this will change the offer to{' '}
              <span className={classes.draftState}>draft </span> mode.
              <br />
              Are you sure you want to edit this offer ?
            </p>
          </Typography>
        }
        open={openEditOfferModal}
        titleAcceptButton={'Edit'}
        titleCancelButton={'Cancel'}
        onAccept={handleEditOfferConfirm}
        onDecline={handleEditOfferCloseModal}
        variant={ModalSizeVariants.small}
        acceptLoading={isLoadingSubmit}
      />
    </div>
  )
}
